import { useState } from "react";
import { toast } from "sonner";
import { Mail, Check, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/lib/i18n";
import { sendLinkByEmail, translateApiError } from "@/lib/api";

const EMAIL_REGEX = /.+@.+\..+/;

export default function EmailShareForm({ id, shareLink, emailEnabled }) {
  const { t, lang } = useLang();
  const [recipient, setRecipient] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSend = async () => {
    if (!EMAIL_REGEX.test(recipient)) return;
    setSending(true);
    try {
      await sendLinkByEmail(id, {
        recipient_email: recipient,
        link: shareLink,
        sender_note: note || null,
        language: lang,
      });
      setSent(true);
      toast.success(t("sent"));
    } catch (e) {
      toast.error(translateApiError(t, e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-black/10 dark:border-white/10">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-neutral-500 mb-4">
        <Mail className="w-3.5 h-3.5" /> {t("sendByEmail")}
      </div>
      <EmailShareBody
        emailEnabled={emailEnabled}
        sent={sent}
        recipient={recipient}
        setRecipient={setRecipient}
        note={note}
        setNote={setNote}
        sending={sending}
        onSend={handleSend}
      />
    </div>
  );
}

function EmailShareBody({ emailEnabled, sent, recipient, setRecipient, note, setNote, sending, onSend }) {
  const { t } = useLang();
  if (!emailEnabled) {
    return <p className="text-xs text-neutral-500 font-mono">{t("emailFeatureDisabled")}</p>;
  }
  if (sent) {
    return (
      <p className="text-[#00ff66] text-sm font-mono uppercase tracking-widest">
        <Check className="w-4 h-4 inline mr-2" /> {t("sent")}
      </p>
    );
  }
  return (
    <div className="space-y-3">
      <Input
        data-testid="recipient-input"
        type="email"
        value={recipient}
        onChange={(e) => setRecipient(e.target.value)}
        placeholder={t("recipient")}
        className="bg-transparent border border-black/10 dark:border-white/10 rounded-none font-mono"
      />
      <Textarea
        data-testid="note-input"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t("note")}
        className="min-h-[80px] bg-transparent border border-black/10 dark:border-white/10 rounded-none font-mono text-sm resize-none"
      />
      <Button
        data-testid="send-email-button"
        onClick={onSend}
        disabled={sending || !recipient}
        className="w-full h-11 rounded-none bg-black dark:bg-white text-white dark:text-black hover:bg-[#00ff66] font-bold uppercase tracking-widest text-xs disabled:opacity-40"
      >
        {sending ? (
          t("sending")
        ) : (
          <span className="inline-flex items-center gap-2">
            <Send className="w-4 h-4" />
            {t("send")}
          </span>
        )}
      </Button>
    </div>
  );
}
